
import { CLASS_ACTIVE, CLASS_SEARCH_OPEN, CLASS_ALLOW_SCROLL } from '../tools/constants/classes';
import { rebindWishlist } from '../objects/swym-wishlist';

class PredictiveSearch extends HTMLElement {
  private input:HTMLInputElement|null;
  private clearButton:HTMLButtonElement|null;
  private predictiveSearchResults:NodeListOf<HTMLElement>|null;

  constructor() {
    super();

    this.input = this.querySelector('[data-search-input]');
    this.clearButton = this.querySelector('[data-search-clear]');
    this.predictiveSearchResults = this.querySelectorAll('[data-search-results-container]');
    
    const isInSearchDrawer = this.closest('[data-search-drawer]');
    
    if (isInSearchDrawer && this.clearButton) {
      setTimeout(() => {
        this.clearButton?.classList.add(CLASS_ACTIVE);
      }, 1000);
    }

    if (!this.input) return;

    this.input.addEventListener(
      'input',
      this.debounce(() => {
        this.onChange();
      }, 300).bind(this)
    );

    this.input.addEventListener('input', () => {
      this.updateClearButtonVisibility();
    });
    
    this.clearButton?.addEventListener('click', () => {
      if(!this.input) return;
      this.input.value = '';
      this.clearButton?.classList.remove(CLASS_ACTIVE);
      this.input.focus(); 
      this.close();
    });
  }

  updateClearButtonVisibility(): void {
    const hasText = this.input && this.input.value.trim();
    
    if (hasText) {
      this.clearButton?.classList.add(CLASS_ACTIVE);
    } else {
      this.clearButton?.classList.remove(CLASS_ACTIVE);
    }
  }

  onChange(): void {
    const searchTerm: string = this.input?.value.trim() || '';

    if (this.input && this.input.value) {
      this.clearButton?.classList.add(CLASS_ACTIVE);
    } else {
      this.clearButton?.classList.remove(CLASS_ACTIVE);
    }

    if (!searchTerm.length) {
      this.close();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm: string): void {
    let filterFor: 'product' | 'article' = 'product';

    this.predictiveSearchResults?.forEach((resultsContainer) => {
      const tabHeadings = resultsContainer.querySelectorAll('[data-tab-heading]') as NodeListOf<HTMLButtonElement>;
      const activeHeading = Array.from(tabHeadings).find((heading) =>
        heading.classList.contains(CLASS_ACTIVE)
      );
      if (activeHeading?.dataset.tabHeading === 'articles') {
        filterFor = 'article';
      }
    });

    fetch(
      `/search/suggest?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search&resources[type]=${filterFor}`
    )
      .then((response: Response) => {
        if (!response.ok) {
          const error = new Error(`${response.status}`);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text: string) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          ?.querySelector('#shopify-section-predictive-search')?.innerHTML;
        if (resultsMarkup) {
          this.predictiveSearchResults?.forEach((result) => (result.innerHTML = resultsMarkup));
          this.open();
          this.attachResultsEvents();
          this.allowScrollingOnResults();
          // Rebind wishlist functionality for the new content
          rebindWishlist();
        }
      })
      .catch((error: unknown) => {
        this.close();
        throw error;
      });
  }

  open(): void {
    const markupLength = this.predictiveSearchResults?.item(0)?.children.length;
    if (markupLength && markupLength > 0) {
      document.body.classList.add(CLASS_SEARCH_OPEN);
      this.predictiveSearchResults?.forEach((result) => result.classList.add(CLASS_ACTIVE));
    } else {
      document.body.classList.remove(CLASS_SEARCH_OPEN);
    }
  }

  close(): void {
    this.predictiveSearchResults?.forEach((result) => result.classList.remove(CLASS_ACTIVE));
    document.body.classList.remove(CLASS_SEARCH_OPEN);
  }

  attachResultsEvents(): void {
    this.predictiveSearchResults?.forEach((resultsContainer) => {
      const tabHeadings = resultsContainer.querySelectorAll('[data-tab-heading]') as NodeListOf<HTMLButtonElement>;
      const tabs = resultsContainer.querySelectorAll('[data-tab]') as NodeListOf<HTMLElement>;

      tabHeadings.forEach((heading) => {
        heading.addEventListener('click', (e: MouseEvent) => {
          e.preventDefault();
          tabHeadings.forEach((offHeading) => offHeading.classList.remove(CLASS_ACTIVE));
          heading.classList.add(CLASS_ACTIVE);

          const openID = heading.dataset.tabHeading;
          if (openID) {
            tabs.forEach((tab) => {
              if (tab.dataset.tab === openID) {
                tab.classList.add(CLASS_ACTIVE);
              } else {
                tab.classList.remove(CLASS_ACTIVE);
              }
            });
            this.onChange();
          }
        });
      });
    });
  }

  allowScrollingOnResults(): void {
    // Allow scrolling on predictive search results even when menu has data-lenis-prevent
    this.predictiveSearchResults?.forEach((result) => {
      const productsContainer = result.querySelector('.o-results__products');
      const articlesContainer = result.querySelector('.o-results__articles');
      
      [productsContainer, articlesContainer].forEach(container => {
        if (container) {
          // Remove lenis prevent attributes
          container.removeAttribute('data-lenis-prevent');
          container.removeAttribute('data-lenis-prevent-wheel');
          container.removeAttribute('data-lenis-prevent-touch');
          
          // Add class to allow scrolling (CSS fallback)
          container.classList.add(CLASS_ALLOW_SCROLL);
          
          // Add manual scroll handling to bypass Lenis
          container.addEventListener('wheel', this.handleManualScroll as EventListener, { passive: false });
          container.addEventListener('touchstart', this.handleTouchStart as EventListener, { passive: false });
          container.addEventListener('touchmove', this.handleTouchMove as EventListener, { passive: false });
        }
      });
    });
  }

  private handleManualScroll = (e: Event): void => {
    const wheelEvent = e as WheelEvent;
    const target = wheelEvent.currentTarget as HTMLElement;
    if (target) {
      wheelEvent.preventDefault();
      wheelEvent.stopPropagation();
      target.scrollTop += wheelEvent.deltaY;
    }
  }

  private touchStartY = 0;
  private handleTouchStart = (e: Event): void => {
    const touchEvent = e as TouchEvent;
    this.touchStartY = touchEvent.touches[0].clientY;
  }

  private handleTouchMove = (e: Event): void => {
    const touchEvent = e as TouchEvent;
    const target = touchEvent.currentTarget as HTMLElement;
    if (target) {
      touchEvent.preventDefault();
      touchEvent.stopPropagation();
      
      const touchY = touchEvent.touches[0].clientY;
      const deltaY = this.touchStartY - touchY;
      
      target.scrollTop += deltaY;
      this.touchStartY = touchY;
    }
  }

  debounce<T extends (...args: any[]) => void>(fn: T, wait: number): (...args: Parameters<T>) => void {
    let t: ReturnType<typeof setTimeout>;
    return (...args: Parameters<T>): void => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define('predictive-search', PredictiveSearch);
